/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Play, Reset, Subtract, WarningFilled } from '@carbon/react/icons';
import { Button, ButtonSkeleton, Loading } from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import urlJoin from 'proper-url-join';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { connect } from 'react-redux';

import { buildIntegrationsUrlCpd } from '@ibm-watson/shared/modules/channels/util/urls';
import {
  CONNECT_TO_AGENT_BEHAVIOR_TYPES,
  LOADING_STATUSES,
  TOOLING_TYPES,
  withWebChatCustomBaseUrl
} from '@ibm-watson/shared/modules/channels/util/web-chat';
import { withV6Router } from '@ibm-watson/shared/modules/ia/components/withV6Router/withV6Router';
import { selectIsDialogEnabledInDraftEnvironment } from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  selectInstanceId,
  getAssistantId,
  getHasSeenDialogNotification,
  selectIamId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { isCpd, isWxoEnabled } from '@ibm-watson/shared/modules/ia/util/config';
import localStorage from '@ibm-watson/shared/modules/ia/util/localStorage';
import { Header } from '../../../../ts/header';
import { ToastStatus } from '../../../ia/store/messages/actions';
import { selectCsrfToken } from '../../../ia/store/session/selectors';
import history from '../../../ia/util/history';
import { ranProcessEvents } from '../../../ia/util/trackers/segment';
import { DialogNotification } from './DialogNotification';

import './WebPreview.scss';

// used as default config values for web chat
const DEFAULT_WEB_CHAT_CONFIG = {
  themeConfig: {
    carbonTheme: 'g10'
  },
  disableSessionHistory: true,
  hideCloseButton: false,
  openChatByDefault: false,
  region: 'dev', // The region your integration is hosted in.
  showLauncher: false,
  showRestartButton: true
};

const DEFAULT_IBM_WEB_CHAT_CONFIG = {
  connectToAgentBehavior: CONNECT_TO_AGENT_BEHAVIOR_TYPES.PREVIEW,
  toolingType: TOOLING_TYPES.ACTIONS,
  isTrackingEnabled: false,
  enableAlphaFeatures: true,
  enableFeatures: {
    restartConversationButton: true,
    alternateSuggestions: true
  }
};

export const strings = {
  RESET: 'Reload',
  CLOSE: 'Close',
  PREVIEW: 'Preview',
  UNABLE_TO_CONNECT_1:
    'Unable to connect to chat service. Use the reload icon ',
  UNABLE_TO_CONNECT_2: ' to refresh the chat window.',
  PREVIEW_BUTTON: 'Preview button section'
};

export class WebPreview extends React.PureComponent {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.previewButtonRef = React.createRef();
    this.webChatInstance = null;
    this.state = {
      open: !!this.props.open,
      webChatLoadingStatus: LOADING_STATUSES.LOADING,
      hasSeenNotification: this.props.hasSeenDialogNotification
    };
  }

  componentDidMount() {
    this._isMounted = true;

    if (!this.props.showingNewIA || this.props.customConfig.integrationID) {
      this.initialWebChat();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async componentDidUpdate(prevProps) {
    const { isConversationalSearch, open } = this.props;
    if (prevProps.open !== open && open !== this.state.open) {
      if (open) {
        this.setState({ open: true });
      } else {
        this.setState({ open: false });
      }
    }
    if (
      !_.isEqual(
        prevProps.customConfig.integrationID,
        this.props.customConfig.integrationID
      ) ||
      !_.isEqual(prevProps.customIBMConfig, this.props.customIBMConfig) ||
      !_.isEqual(prevProps.initiateWebchat, this.props.initiateWebchat)
    ) {
      this.initialWebChat();
    }
    if (
      this.webChatInstance &&
      !_.isEqual(prevProps.isConversationalSearch, isConversationalSearch)
    ) {
      this.webChatInstance.restartConversation();
    }
    if (!_.isEqual(prevProps.messageOnLoad, this.props.messageOnLoad)) {
      await this.addMessageToWebchat();
    }
  }

  onWebChatLoad = async instance => {
    const {
      customConfig,
      messageOnLoad,
      onChatReady,
      inputFieldVisibility = true,
      onCustomResponse,
      onWindowOpen,
      onWindowClose,
      onHistoryEnd,
      onHistoryBegin,
      onReceive,
      onPreReceive,
      onSend,
      onPreSend,
      onPreReset,
      onWebChatMount,
      headerBottomElement,
      iamID
    } = this.props;

    instance.on({
      type: 'pre:restartConversation',
      handler: onPreReset
    });
    if (onChatReady) {
      instance.on({ type: 'chat:ready', handler: onChatReady });
    }
    instance.on({ type: 'restartConversation', handler: this.resetWebChat });
    instance.on({ type: 'pre:send', handler: onPreSend });
    instance.on({ type: 'send', handler: onSend });
    instance.on({ type: 'pre:receive', handler: onPreReceive });
    instance.on({ type: 'receive', handler: onReceive });
    instance.on({ type: 'history:begin', handler: onHistoryBegin });
    instance.on({ type: 'history:end', handler: onHistoryEnd });
    instance.on({ type: 'window:pre:close', handler: this.onPreClose });
    instance.on({ type: 'window:close', handler: onWindowClose });
    instance.on({ type: 'window:open', handler: onWindowOpen });
    instance.on({
      type: 'customResponse',
      handler: onCustomResponse
    });
    await instance.render();
    instance.openWindow();
    instance.updateAssistantInputFieldVisibility(inputFieldVisibility);

    this.addMessageToInstance(instance, messageOnLoad, headerBottomElement);

    if (
      iamID &&
      !customConfig?.integration?.settings?.private?.authorization?.is_on
    ) {
      instance.updateUserID(iamID);
    }

    this.webChatInstance = instance;
    onWebChatMount(instance);
  };

  addMessageToInstance = (instance, messageOnLoad, headerBottomElement) => {
    if (messageOnLoad) {
      const message = { output: messageOnLoad.output };
      instance.addMessage(message, false, null, {
        skipPause: false,
        disableFadeAnimation: true
      });
      // If we have headerBottomElement show it right below of header
      if (headerBottomElement) {
        instance.writeableElements.headerBottomElement.innerHTML =
          ReactDOMServer.renderToString(headerBottomElement);
      } else {
        instance.writeableElements.headerBottomElement.innerHTML = '';
      }
    }
  };

  resetWebChat = () => {
    const { messageOnLoad, onReset, headerBottomElement } = this.props;
    onReset();
    if (this.webChatInstance) {
      if (messageOnLoad) {
        this.addMessageToInstance(
          this.webChatInstance,
          messageOnLoad,
          headerBottomElement
        );
      }
    }
  };

  onMessageError = ({
    errorType,
    errorCode,
    message,
    otherData,
    transactionID
  }) => {
    const { messageActions, onError } = this.props;
    let errorString = `${errorType}: ${message}. Otherdata: ${otherData}`;

    if (transactionID) {
      errorString += ` transactionID: ${transactionID}`;
    }

    messageActions.addMessage({
      message: errorString,
      status: ToastStatus.ERROR
    });
    ranProcessEvents.tryIt(
      {
        category: 'actions - preview panel',
        resultValue: errorString,
        successFlag: 'false'
      }, // NOTE: we don't have access to action data, intent confidence in the error
      {
        messageCategory: 'simple',
        messageDialogError: errorString,
        messageResultCode: errorCode
      }
    );
    onError({ errorType, errorCode, message, otherData });
  };

  initialWebChat = () => {
    const { initiateWebchat = true, messageActions, csrfToken } = this.props;

    if (initiateWebchat) {
      if (this.webChatInstance) {
        this.webChatInstance.destroy();
      }

      let customMessageURL = this.props.customConfig.customMessageURL;
      if (!this.props.showingNewIA) {
        customMessageURL = this.props.customIBMConfig.customMessageURL;
      }

      let customConfig = this.props.customConfig;
      if (isCpd) {
        customConfig = {
          ...this.props.customConfig,
          ...{ cloudPrivateHostURL: buildIntegrationsUrlCpd() }
        };
      }

      customConfig.orchestrateUIAgentExtensions = isWxoEnabled;

      // note: when actions goes to CPD the object passed to createWebChatInstance will need another key,
      // `cloudPrivateHostURL` whose value will be an endpoint variable called webChat.
      // for an example see L120 of /components/integrationPanel/webChat/snippet.js
      this.props
        .createWebChatInstance({
          ...DEFAULT_WEB_CHAT_CONFIG,
          ...customConfig,
          element: this.containerRef.current,
          onLoad: this.onWebChatLoad,
          onError: this.onMessageError,
          __ibm__: {
            botNameOverride: strings.PREVIEW,
            ...DEFAULT_IBM_WEB_CHAT_CONFIG,
            ...this.props.customIBMConfig,
            customMessageURL: customMessageURL,
            /**
             * We must specify the CSRF token as a custom header
             * so that the server correctly validates the message call.
             * We don't need it in the config call
             * because GET calls are not validated by the CSRF token.
             */
            messageHeaders: {
              [Header.X_CSRF_TOKEN]: csrfToken
            }
          }
        })
        .then(
          () =>
            this._isMounted &&
            this.setState({ webChatLoadingStatus: LOADING_STATUSES.LOADED })
        )
        .catch(e => {
          if (this._isMounted) {
            this.setState({ webChatLoadingStatus: LOADING_STATUSES.ERROR });
          }
          messageActions.addErrorMessage(e);
        });
    }
  };

  addMessageToWebchat = async () => {
    if (this.webChatInstance) {
      await this.webChatInstance.restartConversation();
    }
  };

  onPreClose = evt => {
    this.closeWebChat();
    this.props.onWindowPreClose(evt);
  };

  openWebChat = async (evt, passive = false) => {
    await this.props.onPreviewOpen(evt, passive);
    if (this.webChatInstance) {
      this.webChatInstance.openWindow();
    }
    this.setState({ open: true });
  };

  closeWebChat = async (evt, passive = false) => {
    await this.props.onPreviewClose(evt, passive);
    this.setState({ open: false }, () => {
      if (this.previewButtonRef.current) {
        this.previewButtonRef.current.focus();
      }
    });
  };

  routeToAssistantSettings = async () => {
    await this.closeWebChat();
    history.push(
      urlJoin(
        document.baseURI,
        encodeURIComponent(this.props.instanceId),
        'assistants',
        this.props.assistantId,
        'settings'
      )
    );
  };

  dontRemindMeClick = () => {
    localStorage.set(this.props.assistantId, {
      hasSeenDialogNotification: true
    });
    this.setState({ hasSeenNotification: true });
  };

  render() {
    const { open, webChatLoadingStatus, hasSeenNotification } = this.state;
    const { initialLoadingIndicator } = this.props;

    const webChatInitialized = webChatLoadingStatus === LOADING_STATUSES.LOADED;

    return (
      <div className={cx('bx--grid WebPreview', this.props.className)}>
        <section aria-label={strings.PREVIEW_BUTTON}>
          <div className="bx--row">
            <div className="bx--col">
              {open ? (
                <div className="WebPreview__space-holder" />
              ) : !webChatInitialized ? (
                <ButtonSkeleton
                  className="WebPreview__preview-button"
                  size="field"
                />
              ) : (
                <Button
                  id="WebPreview__preview-button"
                  ref={this.previewButtonRef}
                  className="WebPreview__preview-button"
                  kind="tertiary"
                  size="field"
                  iconDescription={strings.PREVIEW}
                  onClick={this.openWebChat}
                  disabled={!webChatInitialized}
                  renderIcon={props => <Play size={24} {...props} />}>
                  {strings.PREVIEW}
                </Button>
              )}
            </div>
          </div>
        </section>
        {this.props.children}
        <div className="bx--row">
          <div className="bx--col">
            <div className="WebPreview__container-anchor">
              <div
                className={cx('WebPreview__container', 'WACOverrides', {
                  open,
                  empty: !webChatInitialized
                })}
                id="WACOverrides"
                ref={this.containerRef}
                aria-hidden={!open}>
                {!webChatInitialized && open && (
                  <React.Fragment>
                    <div
                      id="WebPreview__header"
                      className="WebPreview__header-container">
                      <h5
                        id="WebPreviewFakeWAC__header-name"
                        className="WebPreview__header-name">
                        {strings.PREVIEW}
                      </h5>
                      <div className="WebPreview__header-buttons">
                        <Button
                          id="WebPreviewFakeWAC__header-reset"
                          className="WebPreview__header-button"
                          iconDescription={strings.RESET}
                          aria-label={strings.RESET}
                          renderIcon={Reset}
                          hasIconOnly
                          kind="ghost"
                          size="sm"
                          onClick={this.initialWebChat}
                        />
                        <Button
                          id="WebPreviewFakeWAC__header-close"
                          className="WebPreview__header-button"
                          iconDescription={strings.CLOSE}
                          aria-label={strings.CLOSE}
                          renderIcon={Subtract}
                          hasIconOnly
                          kind="ghost"
                          size="sm"
                          onClick={this.closeWebChat}
                        />
                      </div>
                    </div>
                    <div className="WebPreview__info-container">
                      {initialLoadingIndicator ? (
                        <Loading
                          className="WebPreview__info-loading-indicator"
                          withOverlay={false}
                        />
                      ) : (
                        <>
                          <WarningFilled
                            size={32}
                            className="WebPreview__info-error-icon"
                          />
                          <div className="WebPreview__info-text body-long-01">
                            {strings.UNABLE_TO_CONNECT_1}
                            <Reset size={16} />
                            {strings.UNABLE_TO_CONNECT_2}
                          </div>
                        </>
                      )}
                    </div>
                  </React.Fragment>
                )}
                {open &&
                  this.props.isDialogEnabled &&
                  this.props.className === 'ActionsPreview' &&
                  !hasSeenNotification && (
                    <DialogNotification
                      routeToAssistantSettings={this.routeToAssistantSettings}
                      dontRemindMeClick={this.dontRemindMeClick}
                      className="WebPreview__notification"
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const noop = () => {};

WebPreview.defaultProps = {
  customConfig: {},
  customIBMConfig: {},
  initiateWebchat: true,
  inputFieldVisibility: true,
  isConversationalSearch: false,
  onCustomResponse: noop,
  onError: noop,
  onHistoryBegin: noop,
  onHistoryEnd: noop,
  onPreviewOpen: noop,
  onPreviewClose: noop,
  onPreReceive: noop,
  onPreReset: noop,
  onPreSend: noop,
  onReceive: noop,
  onReset: noop,
  onSend: noop,
  onWebChatMount: noop,
  onWindowClose: noop,
  onWindowOpen: noop,
  onWindowPreClose: noop
};

WebPreview.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  createWebChatInstance: PropTypes.func,
  customConfig: PropTypes.object,
  customIBMConfig: PropTypes.object,
  iamID: PropTypes.string,
  initialLoadingIndicator: PropTypes.bool,
  initiateWebchat: PropTypes.bool,
  isConversationalSearch: PropTypes.bool,
  messageActions: PropTypes.object,
  onChatReady: PropTypes.func,
  onCustomResponse: PropTypes.func,
  onError: PropTypes.func,
  onHistoryBegin: PropTypes.func,
  onHistoryEnd: PropTypes.func,
  onPreReceive: PropTypes.func,
  onPreReset: PropTypes.func,
  onPreSend: PropTypes.func,
  onPreviewOpen: PropTypes.func,
  onPreviewClose: PropTypes.func,
  onReceive: PropTypes.func,
  onReset: PropTypes.func,
  onSend: PropTypes.func,
  onWebChatMount: PropTypes.func,
  onWindowClose: PropTypes.func,
  onWindowOpen: PropTypes.func,
  onWindowPreClose: PropTypes.func,
  open: PropTypes.bool,
  showingNewIA: PropTypes.bool,
  inputFieldVisibility: PropTypes.bool,
  messageOnLoad: PropTypes.object,
  headerBottomElement: PropTypes.element,
  isDialogEnabled: PropTypes.bool,
  instanceId: PropTypes.string,
  assistantId: PropTypes.string,
  hasSeenDialogNotification: PropTypes.bool,
  csrfToken: PropTypes.string
};

const mapStateToProps = state => ({
  isDialogEnabled: selectIsDialogEnabledInDraftEnvironment(state),
  instanceId: selectInstanceId(state),
  assistantId: getAssistantId(state),
  hasSeenDialogNotification: getHasSeenDialogNotification(state),
  iamID: selectIamId(state),
  csrfToken: selectCsrfToken(state)
});

export default withWebChatCustomBaseUrl(
  withV6Router(connect(mapStateToProps)(WebPreview))
);
